import { useEffect, useState } from 'react';
import { API_BASE } from './api-base';
import { PERSONAL_SALE_MOBILE_SPEC, toggleOverride } from './dev-feature-toggles';

/**
 * Client read of the "personal sale" welcome-offer arm — `symphonyPersonalSaleMobile`, the SAME spec the
 * phones conduct. It is the ONE arm the whole feature reads, on every surface.
 *
 * Variant (`true`): the account is IN the sale's ramp. Control (`false`): it is not. This is only the
 * FLAG half — whether the banner/badge actually SHOW is `use-personal-sale.ts`, which also requires the
 * Free plan and the 24h window.
 *
 * It FAILS CLOSED to CONTROL — a gate that has not answered (or errored) shows nothing rather than
 * flashing an unramped promo at everyone, exactly like the sibling gate (`home2-flag`).
 *
 * A dev-tools **Feature toggles** override wins over the conducted value (`toggleOverride` is consulted
 * first) and is inert without a dev-tools grant, so a real user always gets the conducted value — while
 * the feature is being built, turn it on from Dev Tools ▸ Feature toggles (or use the personal-sale
 * preview, which additionally bypasses the plan + window).
 */

/** Matches the sibling gates' bound — a gate that has not answered in time IS control. */
const GATE_TIMEOUT_MS = 1500;

let cachedMobile: Promise<boolean> | null = null;

export function personalSaleMobileEnabled(): Promise<boolean> {
  // Checked on every call, not memoized: the override can be set mid-session from the dev panel.
  const forced = toggleOverride(PERSONAL_SALE_MOBILE_SPEC);
  if (forced !== undefined) return Promise.resolve(forced === 'true');
  if (cachedMobile) return cachedMobile;
  cachedMobile = (async () => {
    try {
      const res = await fetch(`${API_BASE}/api/flags/personal-sale-mobile`, {
        credentials: 'include',
        signal: AbortSignal.timeout(GATE_TIMEOUT_MS),
      });
      if (!res.ok) return false;
      return ((await res.json()) as boolean) === true;
    } catch {
      // Includes the abort: a gate that has not answered in time IS control.
      return false;
    }
  })();
  return cachedMobile;
}

/**
 * Resolve the personal-sale arm for rendering. Starts false — control — so an account not in the variant
 * never sees the banner/badge flash in and out.
 */
export function usePersonalSaleMobileEnabled(): boolean {
  const [flag, setFlag] = useState(false);
  useEffect(() => {
    let live = true;
    void personalSaleMobileEnabled().then((on) => {
      if (live) setFlag(on);
    });
    return () => {
      live = false;
    };
  }, []);
  return flag;
}
